import { Box, CircularProgress, Skeleton, Stack } from "@mui/material"
import { MOTION, STAGGER_STEP } from "../motion"

/**
 * Rows in the shape of the rows that are coming.
 *
 * A wave rather than a pulse, and the rows start their wave in sequence. Both
 * are doing the same job: a spinner says "something is happening somewhere",
 * where a skeleton in the table's own shape says "the table is loading, it will
 * have about this many rows, and it will be here". The layout does not jump when
 * the data lands, because the space was already the right size.
 *
 * The stagger is small on purpose. This is the one animation on screen while
 * somebody waits, and waiting is exactly when an animation with opinions
 * becomes irritating.
 */
export function LoadingRows({ rows = 5, height = 56 }) {
  return (
    <Stack spacing={1}>
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton
          key={i}
          variant="rounded"
          height={height}
          animation="wave"
          /* The wave lives on the pseudo-element, not the root — delaying the
             root delays nothing, which is the kind of stagger that looks like it
             works until you watch it. */
          sx={{ "&::after": { animationDelay: `${i * STAGGER_STEP}ms` } }}
        />
      ))}
    </Stack>
  )
}

/**
 * A spinner, for when there is no shape to imitate.
 *
 * Held back for a beat first. Most requests here answer in well under a second,
 * and a spinner that appears and vanishes inside 200ms is a flash of anxiety
 * about nothing — the screen reads as flickering rather than as fast.
 */
export function LoadingScreen({ minHeight = 240 }) {
  return (
    <Box
      sx={{
        minHeight,
        display: "grid",
        placeItems: "center",
        /* The fade goes on the wrapper. On the spinner itself it would replace
           the rotation MUI puts there, and the result is a quarter-circle that
           fades in and then sits perfectly still. */
        animation: `tw-fade ${MOTION.base}ms ${MOTION.ease} backwards`,
        animationDelay: "250ms",
      }}
    >
      <CircularProgress size={28} />
    </Box>
  )
}
